/**
 * 挑战逻辑（奥特对波）
 *
 * 规则：
 *  - 挑战者 vs 被挑战者
 *  - 五行相克：水克火、火克金、金克木、木克土、土克水
 *  - 相克方能力 −15%；被克方 +10%
 *  - 综合分高者胜（按 effectivePower 比较）
 *  - 10% 概率触发暴击翻盘（弱者暴击 → 强者扣血更多）
 *
 * 这里"对波"是抽象成"一决胜负"——
 * 浮游界面用 dynamic particle animation 展示胜负过程
 */

import type { Element } from "../shared/types";

export interface Fighter {
  scpId: string;
  name: string;
  element: Element;
  rank: number;       // 1-5
  total: number;      // 综合分（fortune.total）
  rankLabel: string;  // "一等" 等
}

/** 五行相克：金→木→土→水→火→金（按隔一个跳） */
const KE: Record<Element, Element> = {
  金: "木", 木: "土", 土: "水", 水: "火", 火: "金",
};

/** 是否存在克制（attacker 克 defender） */
export function isCounter(attacker: Element, defender: Element): boolean {
  return KE[attacker] === defender;
}

/** 元素主色（用于光波，等级 3 默认） */
export const ELEMENT_BEAM_COLOR: Record<Element, string> = {
  金: "#ffe680",
  木: "#88dd66",
  水: "#88c8ff",
  火: "#ff6644",
  土: "#e0c080",
};

/** 元素辅色（用于粒子高光） */
export const ELEMENT_BEAM_LIGHT: Record<Element, string> = {
  金: "#fff8d0",
  木: "#c8f0a0",
  水: "#c8e8ff",
  火: "#ffa060",
  土: "#f0d8a0",
};

/**
 * 等级色阶表（5 等 × 5 元素）
 *  - 1 等：最亮（高光纯色 + 白光）
 *  - 5 等：最暗（深色调）
 * 索引 [rank-1] = 该等级颜色（亮→暗）
 */
const RANK_BEAM_COLOR: Record<Element, [string, string, string, string, string]> = {
  金: ["#fff8d0", "#ffe680", "#d4a847", "#a87830", "#6a4a18"],
  木: ["#c8f0a0", "#88dd66", "#5aaa3a", "#387028", "#2a4818"],
  水: ["#d8f0ff", "#88c8ff", "#5a98d8", "#385878", "#1a3050"],
  火: ["#ffd890", "#ffaa44", "#e85838", "#a83820", "#5a1810"],
  土: ["#f0d8a0", "#e0c080", "#a8804a", "#6a4a28", "#3a2818"],
};

const RANK_BEAM_LIGHT: Record<Element, [string, string, string, string, string]> = {
  金: ["#ffffff", "#fff8d0", "#ffe690", "#d4a860", "#8a6840"],
  木: ["#e0ffa8", "#c8f0a0", "#88c870", "#508838", "#387028"],
  水: ["#ffffff", "#d8f0ff", "#88b8e8", "#5a80b0", "#3060a0"],
  火: ["#ffe8b8", "#ffd090", "#f08858", "#c84030", "#7a2818"],
  土: ["#fff0c8", "#f0d8a0", "#c8a070", "#886848", "#5a4030"],
};

/**
 * 根据等级取光波主色 / 高光
 * @param el 元素
 * @param rank 1=最亮，5=最暗
 */
export function getBeamColorForRank(el: Element, rank: number): { main: string; light: string } {
  const idx = Math.max(0, Math.min(4, rank - 1));
  return {
    main: RANK_BEAM_COLOR[el][idx],
    light: RANK_BEAM_LIGHT[el][idx],
  };
}

/** 胜负原因分类（用于 UI 简报展示） */
export type DuelReasonKind =
  | "rank_gap"        // 等级差距碾压
  | "element_counter" // 元素克制
  | "element_resisted"// 元素被克
  | "even_match"      // 势均力敌
  | "crit_reversal";  // 暴击翻盘

export interface DuelResult {
  winner: Fighter;
  loser: Fighter;
  /** 双方 effective power（含相克系数） */
  winnerPower: number;
  loserPower: number;
  /** 是否触发暴击 */
  crit: boolean;
  /** 是否存在相克 */
  countered: boolean;
  /** 谁克谁（"challenger_kicks_defender" / "defender_kicks_challenger" / null） */
  counterDirection: "challenger_kicks_defender" | "defender_kicks_challenger" | null;
  /** 胜负原因分类 */
  reasonKind: DuelReasonKind;
  /** 胜负原因（人类可读，简短） */
  reason: string;
  /** 详细简报（多行文字，UI 展示） */
  brief: string[];
}

/**
 * 计算战斗结果
 *  - challenger 主动攻击
 *  - 若 challenger 元素克 defender 元素 → challenger 加成
 *  - 反之则 challenger 受到反制
 *  - 10% 概率弱者暴击翻盘
 *  - 返回详细简报（克制 / 暴击 / 等级差 / 势均力敌）
 */
export function simulateDuel(
  challenger: Fighter,
  defender: Fighter
): DuelResult {
  // 1. 相克判定
  let challengerMul = 1.0;
  let defenderMul = 1.0;
  let countered = false;
  let counterDirection: DuelResult["counterDirection"] = null;
  if (isCounter(challenger.element, defender.element)) {
    challengerMul = 1.15; // +15%
    defenderMul = 0.9;    // -10%
    countered = true;
    counterDirection = "challenger_kicks_defender";
  } else if (isCounter(defender.element, challenger.element)) {
    challengerMul = 0.85;
    defenderMul = 1.15;
    countered = true;
    counterDirection = "defender_kicks_challenger";
  }

  const challengerPower = challenger.total * challengerMul + challenger.rank * 2;
  const defenderPower = defender.total * defenderMul + defender.rank * 2;

  // 2. 暴击翻盘（10% 概率）：弱势方随机翻盘
  const crit = Math.random() < 0.10;
  const powerGap = Math.abs(challengerPower - defenderPower);

  let winner: Fighter;
  let loser: Fighter;
  let winnerPower: number;
  let loserPower: number;
  let reasonKind: DuelReasonKind;
  let reason: string;
  const brief: string[] = [];

  // 计算分数（保留两位小数）
  const cp = Math.round(challengerPower * 100) / 100;
  const dp = Math.round(defenderPower * 100) / 100;
  brief.push(`【战力对照】${challenger.name}（${cp}）vs ${defender.name}（${dp}）`);
  brief.push(`【等级对照】${challenger.rankLabel}（${challenger.element}）vs ${defender.rankLabel}（${defender.element}）`);

  if (counterDirection === "challenger_kicks_defender") {
    brief.push(`【五行克制】${challenger.element}克${defender.element}，挑战者得势（+15% / −10%）`);
  } else if (counterDirection === "defender_kicks_challenger") {
    brief.push(`【五行克制】${defender.element}克${challenger.element}，挑战者受制（−15% / +10%）`);
  } else {
    brief.push("【五行】五行无克，势均力敌");
  }

  if (crit && powerGap > 5) {
    // 暴击：分低者胜
    reasonKind = "crit_reversal";
    if (challengerPower < defenderPower) {
      winner = challenger;
      loser = defender;
      winnerPower = challengerPower;
      loserPower = defenderPower;
      reason = `${challenger.name} 暴击翻盘！`;
      brief.push(`【胜负原因】10% 暴击触发，弱者翻盘：${challenger.name} 以弱胜强`);
    } else {
      winner = defender;
      loser = challenger;
      winnerPower = defenderPower;
      loserPower = challengerPower;
      reason = `${defender.name} 暴击，${challenger.name} 惜败！`;
      brief.push(`【胜负原因】10% 暴击触发，${defender.name} 险中求胜`);
    }
    brief.push(`【战力差】差距 ${Math.round(powerGap)} 分（>5 可触发暴击）`);
  } else {
    // 正常：分高者胜
    if (challengerPower >= defenderPower) {
      winner = challenger;
      loser = defender;
      winnerPower = challengerPower;
      loserPower = defenderPower;
    } else {
      winner = defender;
      loser = challenger;
      winnerPower = defenderPower;
      loserPower = challengerPower;
    }

    // 分类原因
    const rankGap = defender.rank - challenger.rank;
    if (countered) {
      reasonKind = counterDirection === "challenger_kicks_defender"
        ? "element_counter"
        : "element_resisted";
      reason = countered
        ? counterDirection === "challenger_kicks_defender"
          ? `${challenger.element}克${defender.element}，${winner.name} 借势胜出`
          : `${defender.element}克${challenger.element}，${winner.name} 顺克取胜`
        : `${winner.name} 胜出`;
      brief.push(`【胜负原因】五行克制决定战局`);
    } else if (Math.abs(rankGap) >= 2) {
      reasonKind = "rank_gap";
      reason = rankGap > 0
        ? `${winner.name} 等级碾压（${rankGap} 等差距）`
        : `${winner.name} 等级碾压（${-rankGap} 等差距）`;
      brief.push(`【胜负原因】等级碾压（差距 ${Math.abs(rankGap)} 等）`);
    } else if (powerGap < 3) {
      reasonKind = "even_match";
      reason = `势均力敌（差距 ${Math.round(powerGap)} 分）`;
      brief.push(`【胜负原因】势均力敌，差距仅 ${Math.round(powerGap)} 分`);
    } else {
      reasonKind = "rank_gap";
      reason = `${winner.name} 综合实力胜出（差距 ${Math.round(powerGap)} 分）`;
      brief.push(`【胜负原因】综合实力胜出（差距 ${Math.round(powerGap)} 分）`);
    }
  }

  return {
    winner,
    loser,
    winnerPower,
    loserPower,
    crit,
    countered,
    counterDirection,
    reasonKind,
    reason,
    brief,
  };
}